/**
 * oddit-section-main-product.js
 * Interactive behaviors for the Main Product section:
 * - Accordion toggle
 *
 * - Thumbnail image gallery with main image switching
 * - Thumbnail strip pagination (up/down arrows via opacity)
 * - Mobile horizontal scroll gallery with peek (native scroll + snap)
 * - UGC video carousel with paginated snap & clickable indicators
 * - Product form AJAX add-to-cart
 *
 */

(function () {
  'use strict';

  function initSection(sectionEl) {
    if (!sectionEl) return;

    /* ==========================================================================
       CLEANUP TRACKING
       ========================================================================== */
    var cleanupFns = [];

    function addListener(target, event, handler, options) {
      target.addEventListener(event, handler, options);
      cleanupFns.push(function () {
        target.removeEventListener(event, handler, options);
      });
    }

    /* ==========================================================================
       DELIVERY ESTIMATE (client-side to avoid Shopify page-cache staleness)
       ========================================================================== */
    var deliveryEl = sectionEl.querySelector('.oddit-section-main-product__delivery[data-delivery-days]');
    if (deliveryEl) {
      var days = parseInt(deliveryEl.getAttribute('data-delivery-days'), 10) || 5;
      var target = new Date();
      target.setDate(target.getDate() + days);

      var weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      var formatted = weekdays[target.getDay()] + ', ' + months[target.getMonth()] + ' ' + target.getDate();

      var dateSpan = deliveryEl.querySelector('.oddit-section-main-product__delivery-date');
      if (dateSpan) dateSpan.textContent = formatted;
    }

    /* ==========================================================================
       ACCORDIONS
       ========================================================================== */
    var accordionTriggers = sectionEl.querySelectorAll('.oddit-section-main-product__accordion-trigger');

    accordionTriggers.forEach(function (trigger) {
      addListener(trigger, 'click', function () {
        var expanded = this.getAttribute('aria-expanded') === 'true';
        var contentId = this.getAttribute('aria-controls');
        var content = contentId ? document.getElementById(contentId) : this.nextElementSibling;

        accordionTriggers.forEach(function (otherTrigger) {
          if (otherTrigger !== trigger) {
            otherTrigger.setAttribute('aria-expanded', 'false');
            var otherId = otherTrigger.getAttribute('aria-controls');
            var otherContent = otherId ? document.getElementById(otherId) : otherTrigger.nextElementSibling;
            if (otherContent) otherContent.hidden = true;
          }
        });

        this.setAttribute('aria-expanded', String(!expanded));
        if (content) content.hidden = expanded;
      });
    });

    /* ==========================================================================
       SELLING PLAN WIDGET
       ========================================================================== */
    var sellingPlanWidget = sectionEl.querySelector('[data-selling-plan-widget]');

    if (sellingPlanWidget) {
      var spSelect = sellingPlanWidget.querySelector('[data-selling-plan-select]');
      var spPriceEl = sellingPlanWidget.querySelector('[data-selling-plan-price]');
      var spComparePriceEl = sellingPlanWidget.querySelector('[data-selling-plan-compare-price]');
      var spSaveLabelEl = sellingPlanWidget.querySelector('[data-selling-plan-save-label]');
      var spDiscountLabelEl = sellingPlanWidget.querySelector('[data-selling-plan-discount-label]');

      var priceBlock = sectionEl.querySelector('[data-price-block]');

      function formatMoney(cents) {
        var amount = cents / 100;
        return amount % 1 === 0 ? '$' + amount.toFixed(0) : '$' + amount.toFixed(2);
      }

      function getSaleConfig() {
        if (!priceBlock) return { active: false, percent: 0 };
        var active = priceBlock.getAttribute('data-sale-active') === 'true';
        var percent = parseInt(priceBlock.getAttribute('data-sale-percent'), 10) || 0;
        return { active: active && percent > 0, percent: percent };
      }

      function getEffectivePrice(variantPrice) {
        var sale = getSaleConfig();
        if (!sale.active) return variantPrice;
        return variantPrice - Math.floor((variantPrice * sale.percent) / 100);
      }

      var onetimePriceEl = sellingPlanWidget.querySelector('[data-onetime-price]');
      var onetimeComparePriceEl = sellingPlanWidget.querySelector('[data-onetime-compare-price]');

      function updateSellingPlanDisplay() {
        var variantPrice = parseInt(sellingPlanWidget.getAttribute('data-variant-price'), 10);
        if (!variantPrice || !spSelect) return;

        var opt = spSelect.options[spSelect.selectedIndex];
        if (!opt) return;

        var basePrice = getEffectivePrice(variantPrice);
        var valueType = opt.getAttribute('data-value-type');
        var value = parseInt(opt.getAttribute('data-value'), 10) || 0;
        var spPrice = basePrice;
        var discountPct = 0;

        if (valueType === 'percentage') {
          discountPct = value;
          spPrice = basePrice - Math.floor((basePrice * discountPct) / 100);
        } else if (valueType === 'fixed_amount') {
          spPrice = basePrice - value;
          discountPct = basePrice > 0 ? Math.floor((value * 100) / basePrice) : 0;
        } else if (valueType === 'price') {
          spPrice = value;
          discountPct = basePrice > 0 ? Math.floor(((basePrice - spPrice) * 100) / basePrice) : 0;
        }

        if (spPriceEl) spPriceEl.textContent = formatMoney(spPrice);
        if (spComparePriceEl) spComparePriceEl.textContent = formatMoney(variantPrice);
        if (spSaveLabelEl) spSaveLabelEl.textContent = 'SAVE ' + discountPct + '%';
        if (spDiscountLabelEl) spDiscountLabelEl.textContent = discountPct + '% OFF every order';
      }

      function updateOnetimeTile() {
        var variantPrice = parseInt(sellingPlanWidget.getAttribute('data-variant-price'), 10);
        if (!variantPrice) return;
        var sale = getSaleConfig();
        var effective = getEffectivePrice(variantPrice);

        if (onetimePriceEl) onetimePriceEl.textContent = formatMoney(effective);
        if (onetimeComparePriceEl) {
          if (sale.active) {
            onetimeComparePriceEl.textContent = formatMoney(variantPrice);
            onetimeComparePriceEl.style.display = '';
          } else {
            onetimeComparePriceEl.style.display = 'none';
          }
        }
      }

      if (spSelect) {
        addListener(spSelect, 'change', updateSellingPlanDisplay);
      }

      // Sync selected-border class to whichever option contains the checked radio.
      // Acts as a fallback for browsers where :has() isn't supported and ensures
      // the class stays in sync when JS-driven changes occur.
      var spOptions = sellingPlanWidget.querySelectorAll('.selling-plan-widget__option');

      function syncSelectedOption() {
        spOptions.forEach(function (opt) {
          var radio = opt.querySelector('.selling-plan-widget__radio');
          opt.classList.toggle('selling-plan-widget__option--selected', !!(radio && radio.checked));
        });
      }

      syncSelectedOption();

      sellingPlanWidget.querySelectorAll('.selling-plan-widget__radio').forEach(function (radio) {
        addListener(radio, 'change', syncSelectedOption);
        addListener(radio, 'change', updateProductDisplayPrice);
      });

      var productPriceEl = sectionEl.querySelector('[data-product-price]');
      var productComparePriceEl = sectionEl.querySelector('[data-product-compare-price]');
      var productSaveBadgeEl = sectionEl.querySelector('[data-product-save-badge]');
      var spAtcButton = sectionEl.querySelector('.oddit-section-main-product__atc-button');
      var spAtcButtonAvailable = spAtcButton.getAttribute('data-available');

      function getSubscriptionPrice() {
        var variantPrice = parseInt(sellingPlanWidget.getAttribute('data-variant-price'), 10);
        var basePrice = getEffectivePrice(variantPrice);
        if (!spSelect) return { price: basePrice, discountPct: 0 };
        var opt = spSelect.options[spSelect.selectedIndex];
        if (!opt) return { price: basePrice, discountPct: 0 };
        var valueType = opt.getAttribute('data-value-type');
        var value = parseInt(opt.getAttribute('data-value'), 10) || 0;
        var spPrice = basePrice;
        var discountPct = 0;
        if (valueType === 'percentage') {
          discountPct = value;
          spPrice = basePrice - Math.floor((basePrice * discountPct) / 100);
        } else if (valueType === 'fixed_amount') {
          spPrice = basePrice - value;
          discountPct = basePrice > 0 ? Math.floor((value * 100) / basePrice) : 0;
        } else if (valueType === 'price') {
          spPrice = value;
          discountPct = basePrice > 0 ? Math.floor(((basePrice - spPrice) * 100) / basePrice) : 0;
        }
        return { price: spPrice, discountPct: discountPct };
      }

      function updateProductDisplayPrice() {
        var subscriptionRadio = sellingPlanWidget.querySelector('[name="purchase_option"][value="subscription"]');
        var isSubscription = subscriptionRadio && subscriptionRadio.checked;
        var variantPrice = parseInt(sellingPlanWidget.getAttribute('data-variant-price'), 10);
        var variantComparePrice = priceBlock
          ? parseInt(priceBlock.getAttribute('data-variant-compare-price'), 10) || 0
          : 0;
        var sale = getSaleConfig();
        var displayPrice;
        var compareDisplay = 0;
        var savePct = 0;

        if (isSubscription) {
          var sub = getSubscriptionPrice();
          displayPrice = sub.price;
          if (variantPrice > 0) {
            compareDisplay = variantPrice;
            savePct = Math.floor(((variantPrice - sub.price) * 100) / variantPrice);
          }
        } else if (sale.active) {
          displayPrice = getEffectivePrice(variantPrice);
          compareDisplay = variantPrice;
          savePct = sale.percent;
        } else if (variantComparePrice > variantPrice) {
          displayPrice = variantPrice;
          compareDisplay = variantComparePrice;
          savePct = Math.floor(((variantComparePrice - variantPrice) * 100) / variantComparePrice);
        } else {
          displayPrice = variantPrice;
        }

        if (productPriceEl) productPriceEl.textContent = formatMoney(displayPrice);

        if (compareDisplay > 0 && savePct > 0) {
          if (productComparePriceEl) {
            productComparePriceEl.textContent = formatMoney(compareDisplay);
            productComparePriceEl.style.display = '';
          }
          if (productSaveBadgeEl) {
            productSaveBadgeEl.textContent = 'save ' + savePct + '%';
            productSaveBadgeEl.style.display = '';
          }
        } else {
          if (productComparePriceEl) productComparePriceEl.style.display = 'none';
          if (productSaveBadgeEl) productSaveBadgeEl.style.display = 'none';
        }

        if (spAtcButton && spAtcButton.hasAttribute('data-atc-label') && spAtcButtonAvailable == 'true') {
          spAtcButton.textContent = spAtcButton.getAttribute('data-atc-label') + ' - ' + formatMoney(displayPrice);
        } else {
          spAtcButton.textContent = 'Sold Out';
          spAtcButton.disabled = true;
        }
      }

      if (spSelect) {
        addListener(spSelect, 'change', updateProductDisplayPrice);
      }

      updateOnetimeTile();
      updateProductDisplayPrice();

      /* ----------------------------------------------------------------------
         AUTO-DETECT AUTOMATIC DISCOUNT
         Shopify automatic discounts (and Recharge stacking discounts) only
         become visible once an item is in the cart. We probe via the Ajax Cart
         API so the PDP can show the same discount the cart shows. Skipped if
         the merchant pinned a sale % manually via section settings.
         ---------------------------------------------------------------------- */
      var PROBE_FLAG_KEY = 'pt:auto_discount_probed';
      var PROBE_PCT_KEY = 'pt:auto_discount_pct';
      var probing = false;

      function applyDetectedDiscount(pct) {
        if (!priceBlock || !(pct > 0)) return;
        if (priceBlock.getAttribute('data-sale-active') === 'true') return;
        priceBlock.setAttribute('data-sale-active', 'true');
        priceBlock.setAttribute('data-sale-percent', String(pct));
        updateOnetimeTile();
        updateSellingPlanDisplay();
        updateProductDisplayPrice();
      }

      function pctFromPrices(originalPrice, finalPrice) {
        if (!originalPrice || originalPrice <= finalPrice) return 0;
        return Math.round(((originalPrice - finalPrice) * 100) / originalPrice);
      }

      function detectFromCart(cart) {
        if (!cart || !cart.items) return 0;
        for (var i = 0; i < cart.items.length; i++) {
          var it = cart.items[i];
          var pct = pctFromPrices(it.original_line_price, it.final_line_price);
          if (pct > 0) return pct;
        }
        return 0;
      }

      function ghostProbe() {
        if (probing) return Promise.resolve(0);
        probing = true;
        var idInput = sectionEl.querySelector('[name="id"]');
        var variantId = idInput ? parseInt(idInput.value, 10) : 0;
        if (!variantId) {
          probing = false;
          return Promise.resolve(0);
        }
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (item) {
            if (!item || !item.key) return 0;
            var pct = pctFromPrices(item.original_price, item.final_price);
            var revertQty = Math.max(0, (item.quantity || 1) - 1);
            return fetch('/cart/change.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: item.key, quantity: revertQty }),
            })
              .then(function () { return pct; })
              .catch(function () { return pct; });
          })
          .catch(function () { return 0; })
          .then(function (pct) {
            probing = false;
            return pct;
          });
      }

      function autoDetectDiscount() {
        if (!priceBlock) return;
        // Only run the probe in 'auto' mode. 'manual' is SSR-pinned; 'off' suppresses entirely.
        var mode = priceBlock.getAttribute('data-sale-mode') || 'auto';
        if (mode !== 'auto') return;

        try {
          var cached = sessionStorage.getItem(PROBE_PCT_KEY);
          if (cached !== null) {
            var cachedPct = parseInt(cached, 10) || 0;
            if (cachedPct > 0) applyDetectedDiscount(cachedPct);
          }
        } catch (e) {}

        fetch('/cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (cart) {
            var pct = detectFromCart(cart);
            if (pct > 0) {
              try { sessionStorage.setItem(PROBE_PCT_KEY, String(pct)); } catch (e) {}
              applyDetectedDiscount(pct);
              return;
            }
            try {
              if (sessionStorage.getItem(PROBE_FLAG_KEY) === '1') return;
              sessionStorage.setItem(PROBE_FLAG_KEY, '1');
            } catch (e) {}
            return ghostProbe().then(function (probedPct) {
              try { sessionStorage.setItem(PROBE_PCT_KEY, String(probedPct)); } catch (e) {}
              if (probedPct > 0) applyDetectedDiscount(probedPct);
            });
          })
          .catch(function () {});
      }

      autoDetectDiscount();
    }

    /* ==========================================================================
       PRODUCT FORM - AJAX ADD TO CART
       ========================================================================== */
    var productForm = sectionEl.querySelector('.oddit-section-main-product__form');

    if (productForm) {
      addListener(productForm, 'submit', function (e) {
        e.preventDefault();

        var atcButton = sectionEl.querySelector('.oddit-section-main-product__atc-button');
        var originalText;
        if (atcButton) {
          atcButton.disabled = true;
          originalText = atcButton.textContent;
          atcButton.textContent = 'adding...';
        }

        var idInput = productForm.querySelector('[name="id"]');
        var variantId = idInput ? parseInt(idInput.value, 10) : null;
        var qtyInput = productForm.querySelector('[name="quantity"]');
        var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

        // Read selling plan: only send if the subscription radio is selected
        var sellingPlanId = null;
        var subscriptionRadio = sectionEl.querySelector('[name="purchase_option"][value="subscription"]');
        var spSelectEl = sectionEl.querySelector('[data-selling-plan-select]');
        if (subscriptionRadio && subscriptionRadio.checked && spSelectEl && spSelectEl.value) {
          sellingPlanId = parseInt(spSelectEl.value, 10) || null;
        }

        if (!variantId || !window.OdditCartDrawer) {
          if (atcButton) {
            atcButton.textContent = originalText;
            atcButton.disabled = false;
          }
          return;
        }

        window.OdditCartDrawer.addToCart(variantId, quantity, {}, sellingPlanId)
          .then(function () {
            if (atcButton) {
              atcButton.textContent = 'added!';
            }
            window.OdditCartDrawer.open();
            setTimeout(function () {
              if (atcButton) {
                atcButton.textContent = originalText;
                atcButton.disabled = false;
              }
            }, 2000);
          })
          .catch(function () {
            if (atcButton) {
              atcButton.textContent = originalText;
              atcButton.disabled = false;
            }
          });
      });
    }

    /* ==========================================================================
       MAIN IMAGE GALLERY
       ========================================================================== */
    var imageContainer = sectionEl.querySelector('.oddit-section-main-product__main-image-container');
    var slides = sectionEl.querySelectorAll('.oddit-section-main-product__main-image-slide');
    var totalSlides = slides.length;
    var currentSlideIndex = 0;

    function isDesktop() {
      return window.innerWidth >= 990;
    }

    /* Remove any embedded gallery video and restore its poster/play button */
    function stopMainVideo() {
      var playing = sectionEl.querySelector('.oddit-section-main-product__main-image-slide--playing');
      if (!playing) return;
      var embedded = playing.querySelector('.oddit-section-main-product__main-image-slide-video');
      if (embedded) embedded.remove();
      playing.classList.remove('oddit-section-main-product__main-image-slide--playing');
    }

    /* Embed and play the video for a given slide (click-to-play, with sound).
       The embedded player keeps its native controls. */
    function playSlideVideo(slide) {
      if (!slide) return;
      if (slide.classList.contains('oddit-section-main-product__main-image-slide--playing')) return;
      if (!window.OdditVideoEmbed) return;

      var btn = slide.querySelector('.oddit-section-main-product__media-play');
      if (!btn) return;

      var videoUrl = btn.getAttribute('data-video-url');
      var videoType = btn.getAttribute('data-video-type');
      if (!videoUrl) return;

      /* Only one gallery video plays at a time */
      stopMainVideo();

      var player = window.OdditVideoEmbed.createPlayer(videoUrl, videoType, { muted: false });
      if (!player) return;

      player.classList.add('oddit-section-main-product__main-image-slide-video');
      slide.classList.add('oddit-section-main-product__main-image-slide--playing');
      slide.appendChild(player);
    }

    function showSlide(index) {
      if (index < 0 || index >= totalSlides) return;
      stopMainVideo();
      currentSlideIndex = index;

      if (isDesktop()) {
        slides.forEach(function (slide) {
          var si = parseInt(slide.getAttribute('data-slide'), 10);
          if (si === index) {
            slide.classList.add('oddit-section-main-product__main-image-slide--active');
          } else {
            slide.classList.remove('oddit-section-main-product__main-image-slide--active');
          }
        });
      } else {
        if (imageContainer && slides[index]) {
          var targetLeft = slides[index].offsetLeft - imageContainer.offsetLeft;
          imageContainer.scrollTo({ left: targetLeft, behavior: 'smooth' });
        }
      }

      syncDesktopThumbs(String(index));
      syncMobileThumbs(String(index));
    }

    /* ==========================================================================
       MOBILE SCROLL LISTENER
       ========================================================================== */
    if (imageContainer) {
      var mobileScrollTimer = null;

      addListener(imageContainer, 'scroll', function () {
        if (isDesktop()) return;
        if (mobileScrollTimer) clearTimeout(mobileScrollTimer);
        mobileScrollTimer = setTimeout(function () {
          var containerLeft = imageContainer.scrollLeft;
          var bestIndex = 0;
          var bestDistance = Infinity;

          slides.forEach(function (slide, i) {
            var slideLeft = slide.offsetLeft - imageContainer.offsetLeft;
            var distance = Math.abs(containerLeft - slideLeft);
            if (distance < bestDistance) {
              bestDistance = distance;
              bestIndex = i;
            }
          });

          if (bestIndex !== currentSlideIndex) {
            stopMainVideo();
            currentSlideIndex = bestIndex;
            syncMobileThumbs(String(bestIndex));
            syncDesktopThumbs(String(bestIndex));
          }
        }, 80);
      });
    }

    /* ==========================================================================
       THUMBNAIL GALLERY (Desktop)
       ========================================================================== */
    var desktopThumbs = sectionEl.querySelectorAll('.oddit-section-main-product__thumbnail');

    desktopThumbs.forEach(function (thumb) {
      addListener(thumb, 'click', function () {
        var index = parseInt(this.getAttribute('data-index'), 10);
        showSlide(index);
      });
    });

    /* ==========================================================================
       THUMBNAIL GALLERY (Mobile)
       ========================================================================== */
    var mobileThumbs = sectionEl.querySelectorAll('.oddit-section-main-product__mobile-thumb');

    mobileThumbs.forEach(function (thumb) {
      addListener(thumb, 'click', function () {
        var index = parseInt(this.getAttribute('data-index'), 10);
        showSlide(index);
      });
    });

    function syncMobileThumbs(index) {
      mobileThumbs.forEach(function (t) {
        t.classList.remove('oddit-section-main-product__mobile-thumb--active');
        if (t.getAttribute('data-index') === index) {
          t.classList.add('oddit-section-main-product__mobile-thumb--active');
        }
      });
    }

    function syncDesktopThumbs(index) {
      desktopThumbs.forEach(function (t) {
        t.classList.remove('oddit-section-main-product__thumbnail--active');
        if (t.getAttribute('data-index') === index) {
          t.classList.add('oddit-section-main-product__thumbnail--active');
        }
      });
    }

    /* ==========================================================================
       THUMBNAIL STRIP PAGINATION (Desktop)
       ========================================================================== */
    var scrollUpBtn = sectionEl.querySelector('.oddit-section-main-product__thumbnail-scroll--up');
    var scrollDownBtn = sectionEl.querySelector('.oddit-section-main-product__thumbnail-scroll--down');
    var thumbViewport = sectionEl.querySelector('.oddit-section-main-product__thumbnail-viewport');

    var THUMB_SCROLL_AMOUNT = 88;

    function setArrowVisible(btn, visible) {
      if (!btn) return;
      btn.style.opacity = visible ? '1' : '0';
      btn.style.pointerEvents = visible ? 'auto' : 'none';
    }

    function updateArrowVisibility() {
      if (!thumbViewport || !scrollUpBtn || !scrollDownBtn) return;

      var scrollTop = thumbViewport.scrollTop;
      var maxScroll = thumbViewport.scrollHeight - thumbViewport.clientHeight;

      setArrowVisible(scrollUpBtn, scrollTop > 2);
      setArrowVisible(scrollDownBtn, maxScroll > 2 && scrollTop < maxScroll - 2);
    }

    if (thumbViewport) {
      addListener(thumbViewport, 'scroll', updateArrowVisibility);
      setTimeout(updateArrowVisibility, 200);
      addListener(window, 'resize', updateArrowVisibility);
    }

    if (scrollDownBtn && thumbViewport) {
      addListener(scrollDownBtn, 'click', function () {
        thumbViewport.scrollBy({ top: THUMB_SCROLL_AMOUNT, behavior: 'smooth' });
        setTimeout(updateArrowVisibility, 400);
      });
    }

    if (scrollUpBtn && thumbViewport) {
      addListener(scrollUpBtn, 'click', function () {
        thumbViewport.scrollBy({ top: -THUMB_SCROLL_AMOUNT, behavior: 'smooth' });
        setTimeout(updateArrowVisibility, 400);
      });
    }

    /* Re-sync visible slide when the viewport crosses the 990px breakpoint,
       since desktop (CSS class toggle) and mobile (scroll position) use
       different display mechanisms. */
    var wasDesktop = isDesktop();
    addListener(window, 'resize', function () {
      var nowDesktop = isDesktop();
      if (nowDesktop !== wasDesktop) {
        wasDesktop = nowDesktop;
        showSlide(currentSlideIndex);
      }
    });

    /* Desktop: horizontal drag changes slide; kill native “ghost” image drag (PT-212) */
    if (imageContainer) {
      sectionEl.querySelectorAll('.oddit-section-main-product__main-image-slide img').forEach(function (img) {
        img.setAttribute('draggable', 'false');
      });
      addListener(
        imageContainer,
        'dragstart',
        function (e) {
          e.preventDefault();
          e.stopPropagation();
        },
        true,
      );
    }

    if (imageContainer && totalSlides > 1) {
      var mainDragActive = false;
      var mainDragStartX = 0;
      var mainLastClientX = 0;
      var mainIsDragging = false;
      var mainJustDragged = false;
      var MAIN_DRAG_SLIDE_THRESHOLD = 30;
      var MAIN_DRAG_ACTIVATE = 5;

      function onMainImageMouseDown(e) {
        if (!isDesktop()) return;
        if (e.button !== 0) return;
        /* Don't hijack interactions with an embedded, playing video */
        if (e.target.closest('.oddit-section-main-product__main-image-slide--playing')) return;
        e.preventDefault();
        mainDragActive = true;
        mainDragStartX = e.clientX;
        mainLastClientX = e.clientX;
        mainIsDragging = false;
        mainJustDragged = false;
      }

      function onMainImageMouseMove(e) {
        if (!mainDragActive || !isDesktop()) return;
        mainLastClientX = e.clientX;
        var dx = e.clientX - mainDragStartX;
        if (!mainIsDragging && Math.abs(dx) > MAIN_DRAG_ACTIVATE) {
          mainIsDragging = true;
          imageContainer.classList.add('oddit-section-main-product__main-image-container--dragging');
        }
        if (mainIsDragging) {
          e.preventDefault();
        }
      }

      function onMainImageMouseUp(e) {
        if (!mainDragActive) return;
        var endX = e && typeof e.clientX === 'number' ? e.clientX : mainLastClientX;
        var dx = endX - mainDragStartX;
        var wasDragging = mainIsDragging;
        mainDragActive = false;
        mainIsDragging = false;
        imageContainer.classList.remove('oddit-section-main-product__main-image-container--dragging');

        if (!isDesktop()) return;

        if (wasDragging && Math.abs(dx) >= MAIN_DRAG_SLIDE_THRESHOLD) {
          mainJustDragged = true;
          setTimeout(function () {
            mainJustDragged = false;
          }, 0);
          if (dx < 0) {
            showSlide(currentSlideIndex + 1);
          } else {
            showSlide(currentSlideIndex - 1);
          }
        }
      }

      addListener(imageContainer, 'mousedown', onMainImageMouseDown);
      addListener(document, 'mousemove', onMainImageMouseMove);
      addListener(document, 'mouseup', onMainImageMouseUp);

      addListener(
        imageContainer,
        'click',
        function (evt) {
          if (mainJustDragged) {
            evt.preventDefault();
            evt.stopPropagation();
          }
        },
        true,
      );
    }

    /* ==========================================================================
       MAIN GALLERY VIDEO PLAYBACK
       ========================================================================== */
    var mediaPlayBtns = sectionEl.querySelectorAll('.oddit-section-main-product__media-play');

    mediaPlayBtns.forEach(function (btn) {
      addListener(btn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        playSlideVideo(btn.closest('.oddit-section-main-product__main-image-slide'));
      });
    });

    /* ==========================================================================
       UGC VIDEO CAROUSEL
       ========================================================================== */
    var carousel = sectionEl.querySelector('.oddit-section-main-product__ugc-carousel');
    var indicators = sectionEl.querySelectorAll('.oddit-section-main-product__ugc-indicator');
    var totalIndicators = indicators.length;

    /* Mouse drag-to-scroll (works with or without indicators) */
    if (carousel) {
      var ugcDragActive = false;
      var ugcDragStartX = 0;
      var ugcDragStartScrollLeft = 0;
      var ugcIsDragging = false;
      var ugcJustDragged = false;
      var UGC_DRAG_THRESHOLD = 5;

      function onUgcMouseDown(e) {
        if (e.button !== 0) return;
        e.preventDefault(); /* Prevent native image drag so our drag-to-scroll works */
        ugcDragActive = true;
        ugcDragStartX = e.clientX;
        ugcDragStartScrollLeft = carousel.scrollLeft;
        ugcIsDragging = false;
        ugcJustDragged = false;
      }

      function onUgcMouseMove(e) {
        if (!ugcDragActive) return;
        var dx = e.clientX - ugcDragStartX;
        if (!ugcIsDragging && Math.abs(dx) > UGC_DRAG_THRESHOLD) {
          ugcIsDragging = true;
          carousel.classList.add('oddit-section-main-product__ugc-carousel--dragging');
        }
        if (ugcIsDragging) {
          e.preventDefault();
          carousel.scrollLeft = ugcDragStartScrollLeft - dx;
        }
      }

      function onUgcMouseUp() {
        if (ugcIsDragging) {
          ugcJustDragged = true;
          setTimeout(function () {
            ugcJustDragged = false;
          }, 0);
        }
        ugcDragActive = false;
        ugcIsDragging = false;
        carousel.classList.remove('oddit-section-main-product__ugc-carousel--dragging');
      }

      addListener(carousel, 'mousedown', onUgcMouseDown);
      addListener(document, 'mousemove', onUgcMouseMove);
      addListener(document, 'mouseup', onUgcMouseUp);

      addListener(
        carousel,
        'click',
        function (e) {
          if (ugcJustDragged) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true,
      );
    }

    if (carousel && totalIndicators > 0) {
      var ugcCurrentPage = 0;
      var ugcScrollTimer = null;

      function getUgcPageScrollPosition(pageIndex) {
        var maxScroll = carousel.scrollWidth - carousel.clientWidth;
        if (maxScroll <= 0) return 0;
        if (totalIndicators <= 1) return 0;
        return (pageIndex / (totalIndicators - 1)) * maxScroll;
      }

      function getUgcCurrentPage() {
        var maxScroll = carousel.scrollWidth - carousel.clientWidth;
        if (maxScroll <= 0) return 0;
        var scrollRatio = carousel.scrollLeft / maxScroll;
        return Math.min(Math.round(scrollRatio * (totalIndicators - 1)), totalIndicators - 1);
      }

      function updateUgcIndicators(pageIndex) {
        ugcCurrentPage = pageIndex;
        indicators.forEach(function (ind, i) {
          ind.classList.toggle('oddit-section-main-product__ugc-indicator--active', i === pageIndex);
        });
      }

      function scrollUgcToPage(pageIndex) {
        var scrollPos = getUgcPageScrollPosition(pageIndex);
        carousel.scrollTo({ left: scrollPos, behavior: 'smooth' });
        updateUgcIndicators(pageIndex);
      }

      addListener(carousel, 'scroll', function () {
        if (ugcScrollTimer) clearTimeout(ugcScrollTimer);
        ugcScrollTimer = setTimeout(function () {
          var page = getUgcCurrentPage();
          updateUgcIndicators(page);
        }, 100);
      });

      indicators.forEach(function (indicator, i) {
        addListener(indicator, 'click', function () {
          scrollUgcToPage(i);
        });
      });

      var ugcTouchStartX = 0;
      var ugcIsSwiping = false;

      addListener(
        carousel,
        'touchstart',
        function (e) {
          ugcTouchStartX = e.changedTouches[0].screenX;
          ugcIsSwiping = true;
        },
        { passive: true },
      );

      addListener(
        carousel,
        'touchend',
        function (e) {
          if (!ugcIsSwiping) return;
          ugcIsSwiping = false;
          var diffX = e.changedTouches[0].screenX - ugcTouchStartX;
          var threshold = 50;

          if (Math.abs(diffX) < threshold) return;

          if (diffX < 0 && ugcCurrentPage < totalIndicators - 1) {
            scrollUgcToPage(ugcCurrentPage + 1);
          } else if (diffX > 0 && ugcCurrentPage > 0) {
            scrollUgcToPage(ugcCurrentPage - 1);
          }
        },
        { passive: true },
      );
    }

    /* ==========================================================================
       UGC VIDEO PLAYBACK
       ========================================================================== */
    var ugcPlayBtns = sectionEl.querySelectorAll('.oddit-section-main-product__ugc-play');

    ugcPlayBtns.forEach(function (btn) {
      addListener(btn, 'click', function () {
        var videoCard = btn.closest('.oddit-section-main-product__ugc-video');
        if (!videoCard || videoCard.classList.contains('oddit-section-main-product__ugc-video--playing')) return;

        var videoUrl = btn.getAttribute('data-video-url');
        var videoType = btn.getAttribute('data-video-type');
        if (!videoUrl) return;

        var overlayStyle = 'position:absolute;inset:0;width:100%;height:100%;z-index:2;';
        var player = window.OdditVideoEmbed.createPlayer(videoUrl, videoType, {
          videoStyle: overlayStyle + 'object-fit:cover;',
          iframeStyle: overlayStyle + 'border:none;',
          muted: false,
        });
        if (!player) return;

        videoCard.classList.add('oddit-section-main-product__ugc-video--playing');
        videoCard.style.position = 'relative';
        videoCard.appendChild(player);

        btn.style.display = 'none';
      });
    });

    /* Return cleanup function */
    return function cleanup() {
      cleanupFns.forEach(function (fn) {
        fn();
      });
      cleanupFns.length = 0;
    };
  }

  /* ==========================================================================
     INITIALIZATION & THEME EDITOR SUPPORT
     ========================================================================== */
  var sectionCleanups = new Map();

  var sections = document.querySelectorAll('.oddit-section-main-product');
  sections.forEach(function (sectionEl) {
    var sectionId = sectionEl.getAttribute('data-section-id');
    var cleanup = initSection(sectionEl);
    if (sectionId && cleanup) sectionCleanups.set(sectionId, cleanup);
  });

  document.addEventListener('shopify:section:load', function (e) {
    var sectionEl = e.target.querySelector('.oddit-section-main-product');
    if (sectionEl) {
      var sectionId = sectionEl.getAttribute('data-section-id');
      var cleanup = initSection(sectionEl);
      if (sectionId && cleanup) sectionCleanups.set(sectionId, cleanup);
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var sectionEl = e.target.querySelector('.oddit-section-main-product');
    if (sectionEl) {
      var sectionId = sectionEl.getAttribute('data-section-id');
      if (sectionId && sectionCleanups.has(sectionId)) {
        sectionCleanups.get(sectionId)();
        sectionCleanups.delete(sectionId);
      }
    }
  });
})();
